import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, User, LogOut, LayoutDashboard, Download, Menu, X } from "lucide-react";
import { useState } from "react";
import { LOGO_URL } from "../lib/brand";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { usePWAInstall } from "../lib/usePWAInstall";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export const Header = () => {
  const { count, setOpen } = useCart();
  const { user, isAdmin, logout } = useAuth();
  const { canInstall, promptInstall } = usePWAInstall();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const links = [
    { label: "Inicio", to: "/" },
    { label: "Catálogo", to: "/#catalogo", testid: "nav-catalog-link" },
    { label: "Contacto", to: "/#contacto" },
  ];

  return (
    <header className="glass-nav sticky top-0 z-50 border-b border-slate-200/80">
      <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" data-testid="nav-brand-logo" className="flex items-center gap-2">
          <img src={LOGO_URL} alt="VALORA" className="h-11 w-auto object-contain" />
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.to}
              data-testid={l.testid}
              onClick={(e) => {
                if (l.to.startsWith("/#")) {
                  e.preventDefault();
                  navigate("/");
                  setTimeout(() => {
                    const el = document.querySelector(l.to.slice(1));
                    el?.scrollIntoView({ behavior: "smooth" });
                  }, 60);
                }
              }}
              className="text-sm font-semibold text-slate-600 transition-colors hover:text-brand-forest"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {canInstall && (
            <button
              data-testid="pwa-install-button"
              onClick={promptInstall}
              className="hidden items-center gap-1.5 rounded-full border border-brand-forest/40 px-3 py-2 text-xs font-bold text-brand-forest transition-colors hover:bg-emerald-50 sm:flex"
            >
              <Download className="h-4 w-4" /> Instalar app
            </button>
          )}

          <button
            data-testid="nav-cart-button"
            onClick={() => setOpen(true)}
            className="relative rounded-full p-2.5 text-brand-navy transition-colors hover:bg-slate-100"
            aria-label="Carrito"
          >
            <ShoppingCart className="h-5 w-5" />
            {count > 0 && (
              <span
                data-testid="nav-cart-badge"
                className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-forest px-1 text-[11px] font-bold text-white"
              >
                {count}
              </span>
            )}
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-user-menu"
                  className="flex h-10 w-10 items-center justify-center rounded-full brand-navy text-sm font-bold text-white"
                >
                  {user.name?.[0]?.toUpperCase() || "U"}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white">
                <DropdownMenuLabel className="truncate">{user.name}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {isAdmin && (
                  <DropdownMenuItem data-testid="nav-admin-button" onClick={() => navigate("/admin")}>
                    <LayoutDashboard className="mr-2 h-4 w-4" /> Panel Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/mis-cursos")}>
                  <User className="mr-2 h-4 w-4" /> Mis pedidos
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-logout-button" onClick={logout}>
                  <LogOut className="mr-2 h-4 w-4" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden items-center gap-2 sm:flex">
              <Link
                to="/login"
                data-testid="nav-login-button"
                className="rounded-full px-4 py-2 text-sm font-semibold text-brand-navy transition-colors hover:bg-slate-100"
              >
                Ingresar
              </Link>
              <Link
                to="/registro"
                data-testid="nav-register-button"
                className="rounded-full brand-navy px-4 py-2 text-sm font-bold text-white transition-transform hover:scale-[1.03]"
              >
                Registrarme
              </Link>
            </div>
          )}

          <button className="p-2 md:hidden" onClick={() => setMobileOpen((v) => !v)} aria-label="Menú">
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-slate-200 bg-white px-4 py-4 md:hidden">
          <a href="#catalogo" onClick={() => setMobileOpen(false)} className="block py-2 font-semibold text-slate-700">
            Catálogo
          </a>
          {canInstall && (
            <button onClick={promptInstall} className="flex w-full items-center gap-2 py-2 font-semibold text-brand-forest">
              <Download className="h-4 w-4" /> Instalar app
            </button>
          )}
          {!user && (
            <div className="mt-2 flex gap-2">
              <Link to="/login" onClick={() => setMobileOpen(false)} className="flex-1 rounded-full border border-slate-300 py-2 text-center font-semibold">
                Ingresar
              </Link>
              <Link to="/registro" onClick={() => setMobileOpen(false)} className="flex-1 rounded-full brand-navy py-2 text-center font-bold text-white">
                Registrarme
              </Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
};
